import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';
import { formatarCNPJ } from '../utils/formatters';
import '../styles/Fgts.css';

const Fgts = () => {
  // Função para gerar período no formato AAAAMM
  const gerarPeriodoAtual = () => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    return `${ano}${mes}`;
  };

  // Função para gerar lista de anos (últimos 3 anos + próximos 2 anos)
  const gerarAnos = () => {
    const anoAtual = new Date().getFullYear();
    const anos = [];
    for (let i = anoAtual - 3; i <= anoAtual + 2; i++) {
      anos.push(i);
    }
    return anos;
  };

  // Função para obter mês e ano do período
  const obterMesAno = (periodo) => {
    if (periodo && periodo.length === 6) {
      return {
        ano: periodo.substring(0, 4),
        mes: periodo.substring(4, 6)
      };
    }
    return { ano: '', mes: '' };
  };

  // Função para atualizar período baseado em mês e ano
  const atualizarPeriodo = (mes, ano) => {
    if (mes && ano) {
      setPeriodo(`${ano}${mes.padStart(2, '0')}`);
    }
  };

  const [activeTab, setActiveTab] = useState('consulta');
  const [empresas, setEmpresas] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [gerandoNovaGuia, setGerandoNovaGuia] = useState(false);
  const [periodo, setPeriodo] = useState(gerarPeriodoAtual());

  // Estados para a tab de guias armazenadas
  const [guias, setGuias] = useState([]);
  const [guiasFiltradas, setGuiasFiltradas] = useState([]);
  const [carregandoGuias, setCarregandoGuias] = useState(false);
  const [filtroGuia, setFiltroGuia] = useState('');
  const [periodoFiltroGuia, setPeriodoFiltroGuia] = useState('');

  // Estados para guias novas
  const [guiasNovas, setGuiasNovas] = useState([]);
  const [carregandoGuiasNovas, setCarregandoGuiasNovas] = useState(false);

  // Filtros para as guias armazenadas
  const [filtrosGuias, setFiltrosGuias] = useState({
    cnpj_empregador: '',
    nome_empregador: '',
    numero_guia: '',
    tipo_guia: '',
    situacao: '',
    periodo: ''
  });

  const [filtros, setFiltros] = useState({
    codigo: '',
    razaosocial: '',
    operador: '',
    cnpj: ''
  });

  // Função para carregar empresas
  const carregarEmpresas = useCallback(() => {
    setCarregando(true);
    setMensagem('');

    const accessToken = localStorage.getItem('access');
    
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/empresas-fgts/`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      })
      .then((res) => {
        if (Array.isArray(res.data)) {
          setEmpresas(res.data);
        } else {
          console.error('Dados recebidos não são um array:', res.data);
          setMensagem('Formato de dados inválido');
        }
        setCarregando(false);
      })
      .catch((error) => {
        console.error('Erro ao carregar empresas:', error);
        const errorMsg = error.response?.data?.erro || 
                         error.response?.data?.mensagem || 
                         error.message || 
                         'Erro desconhecido ao carregar empresas';
        setMensagem(`Erro: ${errorMsg}`);
        setCarregando(false);
      });
  }, []);

  // Função para carregar guias armazenadas
  const carregarGuias = useCallback(() => {
    setCarregandoGuias(true);
    const accessToken = localStorage.getItem('access');
    
    let url = `${process.env.REACT_APP_API_BASE}/api/fgts/listar/`;
    const params = [];
    if (filtroGuia) {
      params.push(`cnpj=${filtroGuia}`);
    }
    if (periodoFiltroGuia) {
      params.push(`periodo=${periodoFiltroGuia}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    axios
      .get(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      })
      .then((res) => {
        if (res.data && res.data.guias) {
          setGuias(res.data.guias);
          setGuiasFiltradas(res.data.guias);
        } else {
          setGuias([]);
          setGuiasFiltradas([]);
        }
        setCarregandoGuias(false);
      })
      .catch((error) => {
        console.error('Erro ao carregar guias:', error);
        setGuias([]);
        setGuiasFiltradas([]);
        setCarregandoGuias(false);
      });
  }, [filtroGuia, periodoFiltroGuia]);

  // Função para carregar guias novas
  const carregarGuiasNovas = useCallback(() => {
    setCarregandoGuiasNovas(true);
    const accessToken = localStorage.getItem('access');
    
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/fgts/listar-novas/`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      })
      .then((res) => {
        if (res.data && res.data.guias) {
          setGuiasNovas(res.data.guias);
        } else {
          setGuiasNovas([]);
        }
        setCarregandoGuiasNovas(false);
      })
      .catch((error) => {
        console.error('Erro ao carregar guias novas:', error);
        setGuiasNovas([]);
        setCarregandoGuiasNovas(false);
      });
  }, []);

  // Aplicar filtros nas guias
  useEffect(() => {
    if (guias.length > 0) {
      const filtered = guias.filter(guia => 
        Object.entries(filtrosGuias).every(([campo, valor]) => {
          if (!valor) return true;
          
          if (campo === 'cnpj_empregador') {
            const cnpjFormatado = formatarCNPJ(guia[campo]);
            return cnpjFormatado.toLowerCase().includes(valor.toLowerCase());
          }
          
          const valorGuia = guia[campo]?.toString().toLowerCase() || '';
          return valorGuia.includes(valor.toLowerCase());
        })
      );
      setGuiasFiltradas(filtered);
    }
  }, [guias, filtrosGuias]);

  useEffect(() => {
    carregarEmpresas();
  }, [carregarEmpresas]);

  useEffect(() => {
    if (activeTab === 'guias') {
      carregarGuias();
    } else if (activeTab === 'guias-novas') {
      carregarGuiasNovas();
    }
  }, [activeTab, carregarGuias, carregarGuiasNovas]);

  // Limpar filtros das guias
  const limparFiltrosGuias = () => {
    setFiltrosGuias({
      cnpj_empregador: '',
      nome_empregador: '',
      numero_guia: '',
      tipo_guia: '',
      situacao: '',
      periodo: ''
    });
  };

  // Função para alternar seleção de empresa
  const handleCheckbox = (cnpj) => {
    setSelecionadas((prev) =>
      prev.includes(cnpj) 
        ? prev.filter((item) => item !== cnpj) 
        : [...prev, cnpj]
    );
  };

  // Selecionar todas
  const selecionarTodas = () => {
    const empresasFiltradas = empresas.filter(emp =>
      Object.entries(filtros).every(([campo, valor]) =>
        emp[campo]?.toString().toLowerCase().includes(valor.toLowerCase())
      )
    );
    if (selecionadas.length === empresasFiltradas.length) {
      setSelecionadas([]);
    } else {
      setSelecionadas(empresasFiltradas.map(emp => emp.cnpj));
    }
  };

  // ================================
  // Gerar Guias FGTS (múltiplas)
  // ================================
  const gerarGuiasFgts = async () => {
    if (selecionadas.length === 0) {
      setMensagem('Selecione ao menos uma empresa');
      return;
    }

    if (!periodo || periodo.length !== 6) {
      setMensagem('Selecione um mês e ano válidos');
      return;
    }

    const accessToken = localStorage.getItem('access');
    setGerando(true);
    setMensagem('Gerando guias FGTS... Este processo pode demorar alguns minutos.');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/fgts/gerar/`,
        { 
          cnpjs: selecionadas,
          periodo: periodo
        },
        { 
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          validateStatus: () => true,
          timeout: 300000 // 5 minutos de timeout
        }
      );

      const contentType = response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);

        if (json.empresas_com_erro) {
          const listaEmpresas = json.empresas_com_erro.map(emp => 
            `CNPJ: ${formatarCNPJ(emp.cnpj)} - ${emp.razaosocial} (${emp.erro})`
          ).join('\n');
          setMensagem(`Erro ao gerar guias para as empresas:\n${listaEmpresas}`);
        } else {
          setMensagem(`Erro: ${json.mensagem || 'Erro desconhecido'}`);
        }
        return;
      }

      // Verificar se há empresas com erro no header
      const empresasComErro = response.headers['x-empresas-com-erro'];
      let mensagemExtra = '';
      if (empresasComErro) {
        const erros = JSON.parse(empresasComErro);
        const listaErros = erros.map(emp => 
          `${formatarCNPJ(emp.cnpj)} - ${emp.razaosocial}`
        ).join(', ');
        mensagemExtra = ` (Algumas empresas tiveram erro: ${listaErros})`;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `guias_fgts_${periodo}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMensagem(`Guias FGTS geradas com sucesso!${mensagemExtra}`);
      
      // Recarregar guias se estiver na tab de guias
      if (activeTab === 'guias') {
        carregarGuias();
      }
      
    } catch (error) {
      console.error('Erro ao gerar guias FGTS:', error);
      if (error.code === 'ECONNABORTED') {
        setMensagem('Timeout: O processo demorou mais que o esperado. Tente novamente com menos empresas.');
      } else {
        setMensagem(`Erro inesperado: ${error.message}`);
      }
    } finally {
      setGerando(false);
    }
  };

  // ================================
  // Gerar Guia Nova (individual)
  // ================================
  const gerarGuiaNova = async () => {
    if (selecionadas.length === 0) {
      setMensagem('Selecione uma empresa para gerar a guia nova');
      return;
    }

    if (selecionadas.length > 1) {
      setMensagem('Selecione apenas uma empresa para gerar guia nova');
      return;
    }

    if (!periodo || periodo.length !== 6) {
      setMensagem('Selecione um mês e ano válidos');
      return;
    }

    const accessToken = localStorage.getItem('access');
    setGerandoNovaGuia(true);
    setMensagem('Gerando nova guia FGTS...');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/fgts/gerar-nova/`,
        { 
          cnpj: selecionadas[0],
          periodo: periodo
        },
        { 
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          validateStatus: () => true,
          timeout: 300000
        }
      );

      const contentType = response.headers['content-type'];

      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        setMensagem(`Erro: ${json.mensagem || 'Erro desconhecido'}`);
        return;
      }

      // Download do PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extrair nome do arquivo do header Content-Disposition se disponível
      const contentDisposition = response.headers['content-disposition'];
      let filename = `guia_nova_fgts_${selecionadas[0]}_${periodo}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMensagem('Guia nova FGTS gerada com sucesso!');
      
      // Recarregar guias se estiver na tab de guias novas
      if (activeTab === 'guias-novas') {
        carregarGuiasNovas();
      }
      
    } catch (error) {
      console.error('Erro ao gerar guia nova:', error);
      if (error.code === 'ECONNABORTED') {
        setMensagem('Timeout: O processo demorou mais que o esperado. Tente novamente.');
      } else {
        setMensagem(`Erro inesperado: ${error.message}`);
      }
    } finally {
      setGerandoNovaGuia(false);
    }
  };

  // Filtrar empresas
  const empresasFiltradas = empresas.filter(emp =>
    Object.entries(filtros).every(([campo, valor]) =>
      emp[campo]?.toString().toLowerCase().includes(valor.toLowerCase())
    )
  );

  // Função para formatar período para exibição
  const formatarPeriodo = (periodo) => {
    if (periodo && periodo.length === 6) {
      const ano = periodo.substring(0, 4);
      const mes = periodo.substring(4, 6);
      return `${mes}/${ano}`;
    }
    return periodo;
  };

  // Função para classificar o status da guia
  const getStatusClass = (diasVencimento, situacao) => {
    if (situacao && situacao.toLowerCase().includes('paga')) return 'text-success';
    if (diasVencimento === null) return 'text-secondary';
    if (diasVencimento <= 0) return 'text-danger';
    if (diasVencimento <= 5) return 'text-warning';
    return 'text-info';
  };

  return (
    <div className="fgts-page">
      <div className="container mt-5 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <i className="fas fa-file-pdf header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">Guias FGTS</h2>
          </div>
        </div>

        {/* Tabs de navegação */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'consulta' ? 'active' : ''}`}
              onClick={() => setActiveTab('consulta')}
            >
              <i className="fas fa-search me-2"></i>
              Consultar Guias
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'guias' ? 'active' : ''}`}
              onClick={() => setActiveTab('guias')}
            >
              <i className="fas fa-file-alt me-2"></i>
              Guias de Arrecadação ({guiasFiltradas.length})
            </button>
          </li>
          <li className="nav-item">
            <button 
              className={`nav-link ${activeTab === 'guias-novas' ? 'active' : ''}`}
              onClick={() => setActiveTab('guias-novas')}
            >
              <i className="fas fa-file-plus me-2"></i>
              Guias Rapidas ({guiasNovas.length})
            </button>
          </li>
        </ul>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* Tab de consulta */}
        {activeTab === 'consulta' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="alert alert-info mb-0">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Atenção:</strong> A geração de guias FGTS pode demorar alguns minutos por empresa.
              </div>

              <div className="d-flex gap-2 align-items-center">
                <div className="d-flex gap-2">
                  <div className="d-flex flex-column">
                    <label className="form-label mb-1 fw-bold">Mês:</label>
                    <select
                      className="form-select"
                      style={{ width: '140px' }}
                      value={obterMesAno(periodo).mes}
                      onChange={(e) => atualizarPeriodo(e.target.value, obterMesAno(periodo).ano)}
                      disabled={gerando || gerandoNovaGuia}
                    >
                      <option value="">Selecione</option>
                      <option value="01">01 - Janeiro</option>
                      <option value="02">02 - Fevereiro</option>
                      <option value="03">03 - Março</option>
                      <option value="04">04 - Abril</option>
                      <option value="05">05 - Maio</option>
                      <option value="06">06 - Junho</option>
                      <option value="07">07 - Julho</option>
                      <option value="08">08 - Agosto</option>
                      <option value="09">09 - Setembro</option>
                      <option value="10">10 - Outubro</option>
                      <option value="11">11 - Novembro</option>
                      <option value="12">12 - Dezembro</option>
                    </select>
                  </div>

                  <div className="d-flex flex-column">
                    <label className="form-label mb-1 fw-bold">Ano:</label>
                    <select
                      className="form-select"
                      style={{ width: '100px' }}
                      value={obterMesAno(periodo).ano}
                      onChange={(e) => atualizarPeriodo(obterMesAno(periodo).mes, e.target.value)}
                      disabled={gerando || gerandoNovaGuia}
                    >
                      <option value="">Ano</option>
                      {gerarAnos().map(ano => (
                        <option key={ano} value={ano}>{ano}</option>
                      ))}
                    </select>
                  </div>

                  <div className="d-flex flex-column justify-content-end">
                    <small className="text-muted mb-1">Período:</small>
                    <div className="bg-light px-3 py-2 rounded text-center border" style={{ minWidth: '80px' }}>
                      <strong className="text-primary">{formatarPeriodo(periodo) || '--/----'}</strong>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column gap-2">
                  <button 
                    className="btn btn-primary px-4"
                    onClick={gerarGuiasFgts}
                    disabled={gerando || gerandoNovaGuia || selecionadas.length === 0 || !periodo || periodo.length !== 6}
                  >
                    {gerando ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Gerando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-pdf me-2"></i>
                        Gerar Guia de Arrecadação
                      </>
                    )}
                  </button>

                  <button 
                    className="btn btn-success px-4"
                    onClick={gerarGuiaNova}
                    disabled={gerando || gerandoNovaGuia || selecionadas.length !== 1 || !periodo || periodo.length !== 6}
                    title={selecionadas.length === 0 ? 'Selecione uma empresa' : 
                           selecionadas.length > 1 ? 'Selecione apenas uma empresa' : 
                           'Gerar guia nova para a empresa selecionada'}
                  >
                    {gerandoNovaGuia ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Gerando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-plus me-2"></i>
                        Gerar Guia Rapida
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Informações sobre seleção */}
            {selecionadas.length > 0 && periodo && periodo.length === 6 && (
              <div className={`alert ${selecionadas.length === 1 ? 'alert-info' : 'alert-warning'}`}>
                <i className="fas fa-info-circle me-2"></i>
                {selecionadas.length === 1 ? (
                  <>
                    <strong>1 empresa selecionada:</strong> Você pode usar "Consultar Guia" ou "Gerar Guia Nova"
                    <br />
                    <small>
                      <strong>Consultar Guia:</strong> Usa a API tradicional (mais completa)
                      <br />
                      <strong>Gerar Guia Nova:</strong> Usa a API rápida (mais simples e rápida)
                    </small>
                  </>
                ) : (
                  <>
                    <strong>{selecionadas.length} empresas selecionadas:</strong> Use "Consultar Guia" para múltiplas empresas
                    <br />
                    <small>Para "Gerar Guia Nova", selecione apenas uma empresa</small>
                  </>
                )}
                <br />
                <small>Período selecionado: <strong>{formatarPeriodo(periodo)}</strong></small>
              </div>
            )}

            <div className="table-responsive">
              {carregando ? (
                <div className="text-center mt-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p>Carregando empresas...</p>
                </div>
              ) : (
                <table className="table table-bordered table-hover text-center align-middle fs-6">
                  <thead className="table-light">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          aria-label="Selecionar todas as empresas"
                          checked={selecionadas.length === empresasFiltradas.length && empresasFiltradas.length > 0}
                          onChange={selecionarTodas}
                        />
                      </th>
                      <th>
                        Código
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtros.codigo}
                          onChange={(e) => setFiltros(prev => ({ ...prev, codigo: e.target.value }))}
                        />
                      </th>
                      <th>
                        Razão Social
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtros.razaosocial}
                          onChange={(e) => setFiltros(prev => ({ ...prev, razaosocial: e.target.value }))}
                        />
                      </th>
                      <th>
                        Operador
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtros.operador}
                          onChange={(e) => setFiltros(prev => ({ ...prev, operador: e.target.value }))}
                        />
                      </th>
                      <th>
                        CNPJ
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtros.cnpj}
                          onChange={(e) => setFiltros(prev => ({ ...prev, cnpj: e.target.value }))}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {empresasFiltradas.map((empresa) => (
                      <tr 
                        key={empresa.cnpj}
                        className={selecionadas.includes(empresa.cnpj) ? 'table-primary' : ''}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selecionadas.includes(empresa.cnpj)}
                            onChange={() => handleCheckbox(empresa.cnpj)}
                          />
                        </td>
                        <td>
                          <span className="badge bg-primary">{empresa.codigo}</span>
                        </td>
                        <td className="text-start">{empresa.razaosocial}</td>
                        <td>
                          <span className="badge bg-secondary">{empresa.operador}</span>
                        </td>
                        <td>{formatarCNPJ(empresa.cnpj)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {!carregando && empresasFiltradas.length === 0 && (
                <div className="text-center mt-4">
                  <p className="text-muted">Nenhuma empresa encontrada.</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Tab de guias armazenadas */}
        {activeTab === 'guias' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-outline-danger btn-sm" 
                  onClick={limparFiltrosGuias}
                  title="Limpar todos os filtros"
                >
                  <i className="fas fa-eraser me-1"></i>
                  Limpar Filtros
                </button>
                
                <div className="input-group" style={{ maxWidth: '200px' }}>
                  <input 
                    type="text" 
                    className="form-control form-control-sm" 
                    placeholder="CNPJ..." 
                    value={filtroGuia}
                    onChange={(e) => setFiltroGuia(e.target.value)}
                  />
                </div>

                <div className="input-group" style={{ maxWidth: '120px' }}>
                  <input 
                    type="text" 
                    className="form-control form-control-sm" 
                    placeholder="202409..." 
                    value={periodoFiltroGuia}
                    onChange={(e) => setPeriodoFiltroGuia(e.target.value)}
                    maxLength="6"
                  />
                </div>

                <button 
                  className="btn btn-outline-primary btn-sm" 
                  type="button"
                  onClick={carregarGuias}
                >
                  <i className="fas fa-search"></i>
                </button>
              </div>
              
              <button 
                className="btn btn-outline-secondary btn-sm" 
                onClick={carregarGuias}
                disabled={carregandoGuias}
              >
                <i className="fas fa-sync-alt me-2"></i>
                Atualizar
              </button>
            </div>
            
            <div className="table-responsive">
              {carregandoGuias ? (
                <div className="text-center mt-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p>Carregando guias...</p>
                </div>
              ) : (
                <table className="table table-bordered table-hover text-center align-middle fs-6">
                  <thead className="table-light">
                    <tr>
                      <th>
                        CNPJ
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtrosGuias.cnpj_empregador}
                          onChange={(e) => setFiltrosGuias(prev => ({ ...prev, cnpj_empregador: e.target.value }))}
                        />
                      </th>
                      <th>
                        Empregador
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtrosGuias.nome_empregador}
                          onChange={(e) => setFiltrosGuias(prev => ({ ...prev, nome_empregador: e.target.value }))}
                        />
                      </th>
                      <th>
                        Período
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="202409"
                          value={filtrosGuias.periodo}
                          onChange={(e) => setFiltrosGuias(prev => ({ ...prev, periodo: e.target.value }))}
                        />
                      </th>
                      <th>
                        Número da Guia
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtrosGuias.numero_guia}
                          onChange={(e) => setFiltrosGuias(prev => ({ ...prev, numero_guia: e.target.value }))}
                        />
                      </th>
                      <th>
                        Tipo
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtrosGuias.tipo_guia}
                          onChange={(e) => setFiltrosGuias(prev => ({ ...prev, tipo_guia: e.target.value }))}
                        />
                      </th>
                      <th>
                        Situação
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Filtrar..."
                          value={filtrosGuias.situacao}
                          onChange={(e) => setFiltrosGuias(prev => ({ ...prev, situacao: e.target.value }))}
                        />
                      </th>
                      <th>Valor</th>
                      <th>Vencimento</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guiasFiltradas.map((guia) => (
                      <tr key={guia.id}>
                        <td>{formatarCNPJ(guia.cnpj_empregador)}</td>
                        <td className="text-start">{guia.nome_empregador}</td>
                        <td>{formatarPeriodo(guia.periodo)}</td>
                        <td>{guia.numero_guia}</td>
                        <td>{guia.tipo_guia}</td>
                        <td>{guia.situacao}</td>
                        <td>R$ {parseFloat(guia.valor_total || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                        <td>{guia.data_limite_pagamento}</td>
                        <td className={getStatusClass(guia.dias_para_vencimento, guia.situacao)}>
                          {guia.situacao && guia.situacao.toLowerCase().includes('paga') ? 'Paga' :
                           guia.dias_para_vencimento === null ? 'N/A' : 
                           guia.dias_para_vencimento <= 0 ? 'Vencida' : 
                           `${guia.dias_para_vencimento} dias`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {!carregandoGuias && guiasFiltradas.length === 0 && (
                <div className="text-center mt-4">
                  <p className="text-muted">
                    {guias.length === 0 ? 'Nenhuma guia encontrada.' : 'Nenhuma guia corresponde aos filtros aplicados.'}
                  </p>
                </div>
              )}
            </div>

            {/* Resumo dos filtros aplicados */}
            {Object.values(filtrosGuias).some(valor => valor !== '') && (
              <div className="mt-3">
                <small className="text-muted">
                  <i className="fas fa-filter me-1"></i>
                  Exibindo {guiasFiltradas.length} de {guias.length} guias
                </small>
              </div>
            )}

            {/* Resumo financeiro */}
            {guiasFiltradas.length > 0 && (
              <div className="mt-3">
                <div className="alert alert-info">
                  <strong>Resumo Financeiro:</strong> 
                  Valor total das guias exibidas: R$ {guiasFiltradas
                    .reduce((total, guia) => total + parseFloat(guia.valor_total || 0), 0)
                    .toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                </div>
              </div>
            )}
          </>
        )}

        {/* Tab de guias novas armazenadas */}
        {activeTab === 'guias-novas' && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Guias Novas Armazenadas</h5>
              <button 
                className="btn btn-outline-secondary btn-sm" 
                onClick={carregarGuiasNovas}
                disabled={carregandoGuiasNovas}
              >
                <i className="fas fa-sync-alt me-2"></i>
                Atualizar
              </button>
            </div>
            
            <div className="table-responsive">
              {carregandoGuiasNovas ? (
                <div className="text-center mt-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p>Carregando guias novas...</p>
                </div>
              ) : (
                <table className="table table-bordered table-hover text-center align-middle fs-6">
                  <thead className="table-light">
                    <tr>
                      <th>CNPJ</th>
                      <th>Empregador</th>
                      <th>Competência</th>
                      <th>Vencimento</th>
                      <th>Trabalhadores</th>
                      <th>Valor Mensal</th>
                      <th>Valor Total</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guiasNovas.map((guia) => (
                      <tr key={guia.id}>
                        <td>{formatarCNPJ(guia.cnpj_empregador)}</td>
                        <td className="text-start">{guia.empregador}</td>
                        <td>{guia.competencia}</td>
                        <td>{guia.data_vencimento}</td>
                        <td>{guia.quantidade_trabalhadores}</td>
                        <td>R$ {parseFloat(guia.valor_mensal || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                        <td>R$ {parseFloat(guia.valor_total || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                        <td className={getStatusClass(guia.dias_para_vencimento)}>
                          {guia.dias_para_vencimento === null ? 'N/A' : 
                           guia.dias_para_vencimento <= 0 ? 'Vencida' : 
                           `${guia.dias_para_vencimento} dias`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              
              {!carregandoGuiasNovas && guiasNovas.length === 0 && (
                <div className="text-center mt-4">
                  <p className="text-muted">Nenhuma guia nova encontrada.</p>
                </div>
              )}
            </div>

            {/* Resumo financeiro das guias novas */}
            {guiasNovas.length > 0 && (
              <div className="mt-3">
                <div className="alert alert-info">
                  <strong>Resumo:</strong> 
                  {guiasNovas.length} guia(s) nova(s) | 
                  Valor total: R$ {guiasNovas
                    .reduce((total, guia) => total + parseFloat(guia.valor_total || 0), 0)
                    .toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Fgts;